/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { useCallback, useRef } from "react";

export const useResettableTimeout = (): {
  resetTimeout: (delayedAction: () => any, delayMs?: number) => void;
  clearOutstandingTimeout: () => void;
} => {
  const outstandingTimeout = useRef<number | NodeJS.Timeout | undefined>(
    undefined
  ); // TS compiler seems confused if setTimeout returns number or NodeJS.Timeout so we accept either

  const resetTimeout = useCallback(
    (delayedAction: () => any, delayMs?: number): void => {
      clearTimeout(outstandingTimeout.current);

      outstandingTimeout.current = setTimeout(() => {
        delayedAction();
      }, delayMs);
    },
    []
  );

  const clearOutstandingTimeout = useCallback(() => {
    clearTimeout(outstandingTimeout.current);
  }, []);

  return {
    resetTimeout,
    clearOutstandingTimeout,
  };
};
